import React from "react";
import Creatable from "react-select/lib/Creatable";
import Select,{components} from "react-select";
import { withStyles } from "@material-ui/core";
import ArrowDropDown from "@material-ui/icons/KeyboardArrowDown";
import Clear from "@material-ui/icons/CancelOutlined";
import { isEqual } from "lodash/lang";

const style = theme => ({
    Dropdown:{
        backgroundColor: theme.ModalBGColor,
        color: theme.TextColor,
        maxWidth:300
    },
    //these classes will be applied on input control container(not options menu)
    reactSelect:{
        '& .select__control':{
            backgroundColor: "transparent",
            color: theme.TextColor,
            maxWidth:300,
            minWidth: 130,
            minHeight: 24,
            maxHeight: "max-content",
            boxSizing:"border-box",
            borderRadius: 2,
            marginTop:6,
            border: `1px solid ${theme.iconColor}`,
            fontSize: 12,
            cursor: "pointer",
            '&:hover,&.select__control--is-focused':{
                boxShadow: "none",
                borderRadius: 2,
                border: `1px solid ${theme.iconColor}`,
            },
            '& .select__value-container':{
                height: 22,
                color: theme.TextColor,
                padding:"0px 8px",
                '& .select__input':{
                    height:22,
                    color: theme.TextColor,
                    '&>input':{
                        height:22
                    }
                },
                '&>div':{
                    margin:0,
                    padding:0
                },
            },
            '& .select__single-value':{
                color: "inherit"
            },
            '& .select__indicators':{
                height: 22,
                '& .select__indicator':{
                    padding: 5,
                    height: 24,
                },
                '& .select__indicator-separator':{
                    height: 24,
                    margin: 0,
                    display: "none"
                }
            },
            '& .select__value-container--is-multi':{
                paddingTop:0,
                paddingBottom:0,
                '&.select__value-container':{
                    height: "max-content",
                    '& .select__multi-value':{
                        backgroundColor: theme.AppBGColor,
                        color: theme.TextColor,
                        margin:"2px",
                        '& .select__multi-value__label':{
                            color: theme.TextColor,
                        }
                    },
                }
            },
            '& .select__loading-indicator':{
                height: 'unset !important',
                '& span': {
                    backgroundColor: theme.HeaderSelectedText,
                },
            }
        },
    },
    iconStyle:{
        color: theme.iconColor,
        cursor: "pointer"
    },
    //these classes will be applied on only menu options(required as we are now using portal)
    selectMenuStyles: {
        '& .select__menu': {
            margin: 0,
            maxHeight: 140,
            borderRadius: 2,
            border: `1px solid ${theme.iconColor}`,
            '& .select__menu-list': {
                maxHeight: 138,
                borderRadius: 2,
                fontSize:theme.textFontSize,
                backgroundColor: theme.ModalBGColor,
                color: theme.TextColor,
            },
            '& .select__option > div': {
                wordBreak:"break-all"
            },
            '& .select__option--is-focused':{
                backgroundColor: theme.HighlightColor,
                cursor: "pointer",
            },
            '& .select__option--is-selected':{
                backgroundColor: theme.HeaderSelectedText
            }
        }
    },
})

const getCustomReactPortalClasses = (theme, selectMenuStyles) => {
  const menuStyles = style(theme).selectMenuStyles;
  const menuClass = '& .select__menu', menuListClass = '& .select__menu-list';
  return {
    zIndex: 9999,
    [menuClass]: {
      ...menuStyles[menuClass],
      ...selectMenuStyles[menuClass],
      [menuListClass]: {
        ...menuStyles[menuClass][menuListClass],
        ...selectMenuStyles[menuClass][menuListClass],
      },
    },
  };
};

class SelectComponent extends React.Component{
    constructor(props){
        super(props);
        this.canAcceptNewValue = false;
        this.options = [];
        this.state = {
            value: { label: this.props.value?.toString() || null, value: null},
            inputValue:""
        }
        props.inputRef && (props.inputRef.current = this);
    }

    componentDidMount(){
        this.updateSelectOptions();
    }

    componentDidUpdate(prevProps){        
        if(prevProps.column.colDef.field != this.props.column.colDef.field || !isEqual(this.props.data,prevProps.data)){
            this.updateSelectOptions();
        }
        if( this.props.value !== prevProps.value){
            this.updateSelectedValue();
        }
    }

    updateSelectedValue = () =>{
       const value = this.options.find(option => option.label === this.props.value || option.value === this.props.value);
       this.setState({
            value: value || null
        }, this.updateParent);
    }

    updateSelectOptions = () => {
        const { colDef } = this.props.column || {};
        if(colDef){
            this.canAcceptNewValue = colDef.cellEditorParams && colDef.cellEditorParams.canAcceptNewValue;
            const { isMultiSelection , noNullValue } = colDef.cellEditorParams || {};
            if(noNullValue){
                this.options = this.props.data.filter(option => option.value !== null);
            } else{
                this.options = this.props.data;
            }

            let value = null;
            if(isMultiSelection){
                const labels = this.props.value && this.props.value.split(",").map(value => value.trim());
                value = labels ? this.options.filter(option => labels.includes(option.label.trim())) : null;
            } else{
                const selectedValue = this.state.value  ||  { label: this.props.value || null, value: null};
                value = this.options.find((option) =>
                  [option.label?.toString(), option.value?.toString()].includes(
                    selectedValue.label?.toString()
                  )
                );
            }

            this.setState({
                value: value || null
            }, this.updateParent)
        }
    }

    getValue = () => {
        return this.state.value ?? "";
    }

    handleChange = (value) => {
        if(this.props.isMultiSelection){
            value = value && value.length ? value :null;
            this.setState({
                value
            }, this.updateParent)

        } else {
            if(value){
                this.setState({
                    value:{
                        ...value,
                        label: value.label,
                        value: value.value,
                        isNewValue: Boolean(value.__isNew__)
                    }
                },this.updateParent);
            } else {
                this.setState({
                    value
                }, this.updateParent)
            }
        }

    }

    onInputChange = (value) => {
        const { colDef } = this.props.column || {};
        const { capitalize, inputFilter } = colDef?.cellEditorParams || {};
        let filteredValue = value;
        if (typeof inputFilter === 'function') {
            filteredValue = inputFilter(value);
        } else if (capitalize) {
            filteredValue = value ? value.toUpperCase() : value;
        }
        this.setState({ inputValue: filteredValue });
    }

    updateParent(){
        if(this.props.onChange){
            this.props.onChange(this.state.value)
        }
    }

    filterOption = (option, filterValue) => {
        const searchParamsList = [option.label, option?.value, option.data?.labelSuffix]
        return searchParamsList.some((params) =>
          params
            ?.toString()
            .toUpperCase()
            .includes(filterValue.toString().toUpperCase())
        );
    }

    // Returns filtered options merged with any exact-match from allOptions that is missing from the current filtered list.
    getEffectiveOptions = (allOptions, inputValue) => {
        const injected = (inputValue && allOptions?.length)
            ? allOptions.filter((o) =>
                o.value === inputValue &&
                !this.options.some((opt) => opt.value === o.value)
              )
            : [];
        return injected.length > 0 ? [...this.options, ...injected] : this.options;
    }

    isValidNewOption = (inputValue, selectOptions) => {
        const { colDef } = this.props.column || {};
        const { format = /./, maxLength, allOptions } = colDef?.cellEditorParams || {};
        const formatValid = format?.test?.(inputValue);
        const lengthValid = !maxLength || inputValue.length <= maxLength;
        if (!inputValue || !formatValid || !lengthValid) return false;
        if (selectOptions.findIndex(option => option.label === inputValue) >= 0) return false;
        // Block creation if typed value matches an existing option's value in the full master list.
        if (allOptions?.some((o) => o.value === inputValue)) return false;
        return true;
    }

    noOptionsMessage = () =>{
        if(this.canAcceptNewValue){
            const { colDef } = this.props.column || {};
            const placeholder = colDef?.cellEditorParams?.placeholder || "";
            return `${placeholder}`;
        }
        return "No options"
    }

    getPlaceholderText = () => {
        if (this.props.isLoading) {
            return "Loading...";
        }
        return this.props.placeholder ?? "Select...";
    }

    render(){
        let selectStyles = {
            menu: (base) => ({ ...base, zIndex: 2 }) // Set higher z-index to appear above ag-grid group headers
        };
        const { classes, theme, usePortal=false, customReactSelectClass=null, selectMenuStyles={} } = this.props;
        const Dropdown = this.canAcceptNewValue ? Creatable : Select;
        const { colDef } = this.props.column || {};
        const createLabel = colDef?.cellEditorParams?.createLabel;
        const formatCreateLabel = createLabel
          ? (inputValue) => `Add ${createLabel} '${inputValue}'`
          : undefined;

        // allOptions: full unfiltered list; only active when canAcceptNewValue is true.
        // Blocks duplicate creation and injects missing exact-match options back into the visible list.
        const allOptions = colDef?.cellEditorParams?.allOptions;
        const effectiveOptions = this.getEffectiveOptions(allOptions, this.state.inputValue);

        let isClearable = true;
        if (colDef.cellEditorParams) {
            if (colDef.cellEditorParams.isClearable !== undefined) {
                isClearable = colDef.cellEditorParams.isClearable;
            }
        }
        
        if (usePortal){
            selectStyles = {
                ...selectStyles,
                menuPortal: (base) => ({ ...base, ...getCustomReactPortalClasses(theme, selectMenuStyles)})
              };
        }
        
        // For ag-grid cell editor, we need to apply the Dropdown class to the container
        const containerClass = this.props.bulkEditContainer ? "" : classes.Dropdown;
        
        return (
            <Dropdown
                styles={selectStyles}
                placeholder={this.getPlaceholderText()}
                autoFocus={this.props.autoFocus ?? true}
                isMulti={this.props.isMultiSelection}
                isClearable={isClearable}
                value={this.state.value}
                inputValue={this.state.inputValue}
                onInputChange={this.onInputChange}
                onChange={this.handleChange}
                options={effectiveOptions}
                classNamePrefix={"select"}
                className={`${classes.reactSelect} ${classes.selectMenuStyles} ${customReactSelectClass} ${containerClass}`}
                menuPlacement="auto"
                isLoading={this.props.isLoading}
                isDisabled={this.props.isLoading}
                filterOption={this.filterOption}
                isValidNewOption={this.isValidNewOption}
                formatCreateLabel={formatCreateLabel}
                closeMenuOnSelect={!this.props.isMultiSelection}
                noOptionsMessage={this.noOptionsMessage}
                openMenuOnFocus={this.props?.openMenuOnFocus ?? false}
                menuPortalTarget={usePortal? document.getElementById("appContainer"): null}
                components = {{
                    DropdownIndicator: props => <ArrowDropDown {...props.innerProps} className={classes.iconStyle} />,
                    ClearIndicator :props => <Clear {...props.innerProps} className={classes.iconStyle} />,
                    Option: props =>  <components.Option {...props}>
                                            <div>{props.data.label} {props.data.labelSuffixNewLine ? <><br/>{props.data.labelSuffix}</>: props.data.labelSuffix}</div>
                                        </components.Option>
                }}
            />
        )
    }
}

export default withStyles(style, { withTheme: true })(SelectComponent);